import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/client';
import { useTranslation } from '../i18n';
import type { ParkingSpotAttributes } from '@lotngo/shared';
import {
  PARKING_STRUCTURE_TYPES,
  VEHICLE_SIZES,
  DEPENDENCY_STATUSES,
} from '@lotngo/shared';

// ─── types ────────────────────────────────────────────────────────────────────

interface SpotDetailResp {
  id: string;
  spot_number: string;
  level_label: string;
  level_code: string;
  attributes: ParkingSpotAttributes;
}

type AttrKey = keyof ParkingSpotAttributes;

interface AttrField {
  attr:        AttrKey;
  labelKey:    string;
  options:     readonly string[];
  prefix:      string;
  icon:        string;
}

const FIELDS: AttrField[] = [
  { attr: 'structure_type',    labelKey: 'attr_structure',    options: PARKING_STRUCTURE_TYPES, prefix: 'structure_',  icon: 'directions_car' },
  { attr: 'vehicle_size',      labelKey: 'attr_vehicle_size', options: VEHICLE_SIZES,           prefix: 'vehicle_',    icon: 'straighten' },
  { attr: 'dependency_status', labelKey: 'attr_dependency',   options: DEPENDENCY_STATUSES,     prefix: 'dependency_', icon: 'link' },
];

// ─── EditAttributesPage ──────────────────────────────────────────────────────

export default function EditAttributesPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { t } = useTranslation();

  const { data: spot, isLoading } = useQuery({
    queryKey: ['spot', id],
    queryFn:  () => apiClient.get<{ data: SpotDetailResp }>(`/spots/${id}`).then((r) => r.data.data),
    enabled:  !!id,
  });

  const [form, setForm] = useState<ParkingSpotAttributes | null>(null);
  useEffect(() => {
    if (spot?.attributes && !form) setForm(spot.attributes);
  }, [spot, form]);

  const saveMutation = useMutation({
    mutationFn: (payload: Partial<ParkingSpotAttributes>) =>
      apiClient.patch(`/spots/${id}/attributes`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['spot', id] });
      qc.invalidateQueries({ queryKey: ['mySpots'] });
      qc.invalidateQueries({ queryKey: ['spots', 'free'] });
      navigate(`/spots/${id}`);
    },
  });

  if (isLoading || !spot || !form) {
    return (
      <div style={page}>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#586064' }}>
          …
        </div>
      </div>
    );
  }

  const hasUnknown = FIELDS.some((f) => form[f.attr] === 'unknown');
  const dirty = (Object.keys(form) as AttrKey[]).some(
    (k) => form[k] !== spot.attributes[k],
  );
  const spotLabel = `${t(spot.level_code) || spot.level_label} · ${spot.spot_number}`;

  return (
    <div style={page}>
      {/* ── Top bar ── */}
      <header style={topBar}>
        <button
          onClick={() => navigate(-1)}
          style={iconBtn}
          aria-label="Close"
        >
          <span className="material-symbols-outlined" style={{ fontSize: '24px' }}>close</span>
        </button>

        <div style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', pointerEvents: 'none' }}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 114 26"
            style={{ height: '26px', width: 'auto', display: 'block' }}
            aria-label="Lot'N'Go"
          >
            <text
              x="57" y="20"
              fontFamily="Manrope, system-ui, sans-serif"
              fontWeight="700" fontSize="20"
              fill="#005bc0" textAnchor="middle" letterSpacing="-0.4"
            >
              {"Lot'N'Go"}
            </text>
          </svg>
        </div>

        <div style={{ width: '40px', flexShrink: 0 }} />
      </header>

      {/* ── Body ── */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 'calc(80px + env(safe-area-inset-top, 0px)) 20px 120px' }}>

        {/* Spot info card */}
        <div style={spotCard}>
          <span style={eyebrow}>{t('attr_edit_title')}</span>
          <h2 style={spotTitle}>{spotLabel}</h2>
          <p style={subtitle}>{t('attr_edit_subtitle')}</p>
        </div>

        {/* Info notice (cool-grey, matches MarkFreePage's release_spot_info pattern) */}
        {hasUnknown && (
          <div style={infoCard}>
            <span
              className="material-symbols-outlined"
              style={{ color: '#005bc0', fontSize: '20px', flexShrink: 0, marginTop: '1px' }}
            >
              info
            </span>
            <p style={{ fontSize: '12px', fontWeight: 500, color: '#505157', lineHeight: 1.55, margin: 0 }}>
              {t('attr_missing_banner')}
            </p>
          </div>
        )}

        {/* Attribute sections — each one is its own white card */}
        {FIELDS.map((f) => (
          <section key={f.attr} style={attrCard}>
            <div style={attrHeader}>
              <span
                className="material-symbols-outlined"
                style={{ color: '#005bc0', fontSize: '20px' }}
                aria-hidden
              >
                {f.icon}
              </span>
              <h3 style={attrTitle}>{t(f.labelKey)}</h3>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {f.options.map((opt) => {
                const active = form[f.attr] === opt;
                const isUnknown = opt === 'unknown';
                return (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => setForm({ ...form, [f.attr]: opt } as ParkingSpotAttributes)}
                    style={{
                      ...optionRow,
                      ...(active ? optionRowActive : {}),
                      ...(isUnknown && !active ? { color: '#8b9296', fontStyle: 'italic' } : {}),
                    }}
                  >
                    <span>{t(`${f.prefix}${opt}`)}</span>
                    <span
                      className="material-symbols-outlined"
                      style={{
                        fontSize: '20px',
                        color: active ? '#005bc0' : 'transparent',
                        fontVariationSettings: "'FILL' 1, 'wght' 500, 'GRAD' 0, 'opsz' 24",
                      }}
                      aria-hidden
                    >
                      check_circle
                    </span>
                  </button>
                );
              })}
            </div>
          </section>
        ))}
      </div>

      {/* ── Fixed bottom: Save ── */}
      <div style={bottomBar}>
        <button
          onClick={() => saveMutation.mutate(form)}
          disabled={!dirty || saveMutation.isPending}
          style={{
            ...primaryCta,
            background: (!dirty || saveMutation.isPending)
              ? '#c7d5e8'
              : 'linear-gradient(135deg, #005bc0 0%, #004fa9 100%)',
            boxShadow: (!dirty || saveMutation.isPending)
              ? 'none'
              : '0 12px 32px -4px rgba(0,91,192,0.3)',
            cursor: (!dirty || saveMutation.isPending) ? 'not-allowed' : 'pointer',
          }}
        >
          {saveMutation.isPending ? t('attr_saving') : t('attr_save')}
        </button>
      </div>
    </div>
  );
}

// ─── styles ──────────────────────────────────────────────────────────────────

const page: React.CSSProperties = {
  display:         'flex',
  flexDirection:   'column',
  minHeight:       '100dvh',
  maxWidth:        '430px',
  margin:          '0 auto',
  backgroundColor: '#f8f9fa',
  fontFamily:      "'Inter', system-ui, -apple-system, sans-serif",
  position:        'relative',
};

const topBar: React.CSSProperties = {
  position:             'fixed',
  top:                  0,
  left:                 '50%',
  transform:            'translateX(-50%)',
  width:                '100%',
  maxWidth:             '430px',
  height:               'calc(64px + env(safe-area-inset-top, 0px))',
  paddingTop:           'env(safe-area-inset-top, 0px)',
  paddingLeft:          '16px',
  paddingRight:         '16px',
  display:              'flex',
  alignItems:           'center',
  justifyContent:       'space-between',
  backgroundColor:      'rgba(248,249,250,0.85)',
  backdropFilter:       'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  boxShadow:            '0 12px 32px -4px rgba(43,52,55,0.06)',
  zIndex:               50,
  boxSizing:            'border-box',
};

const iconBtn: React.CSSProperties = {
  width:          '40px',
  height:         '40px',
  border:         'none',
  background:     'none',
  cursor:         'pointer',
  display:        'flex',
  alignItems:     'center',
  justifyContent: 'center',
  borderRadius:   '9999px',
  color:          '#005bc0',
  flexShrink:     0,
};

const spotCard: React.CSSProperties = {
  background:   '#ffffff',
  borderRadius: '20px',
  padding:      '20px 22px',
  boxShadow:    '0 8px 24px -4px rgba(43,52,55,0.06)',
  marginBottom: '16px',
};

const eyebrow: React.CSSProperties = {
  fontSize:      '10px',
  fontWeight:    700,
  textTransform: 'uppercase',
  letterSpacing: '0.1em',
  color:         '#005bc0',
  display:       'block',
  marginBottom:  '6px',
};

const spotTitle: React.CSSProperties = {
  fontFamily:    "'Manrope', system-ui, sans-serif",
  fontSize:      '24px',
  fontWeight:    800,
  color:         '#2b3437',
  margin:        '0 0 10px',
  lineHeight:    1.2,
  letterSpacing: '-0.02em',
};

const subtitle: React.CSSProperties = {
  fontSize:   '13px',
  color:      '#586064',
  margin:     0,
  lineHeight: 1.5,
};

const infoCard: React.CSSProperties = {
  display:      'flex',
  alignItems:   'flex-start',
  gap:          '12px',
  padding:      '14px 16px',
  borderRadius: '14px',
  background:   'rgba(226,226,233,0.4)',
  marginBottom: '20px',
};

const attrCard: React.CSSProperties = {
  background:   '#ffffff',
  borderRadius: '20px',
  padding:      '18px 18px 14px',
  boxShadow:    '0 8px 24px -4px rgba(43,52,55,0.06)',
  marginBottom: '16px',
};

const attrHeader: React.CSSProperties = {
  display:      'flex',
  alignItems:   'center',
  gap:          '10px',
  marginBottom: '12px',
};

const attrTitle: React.CSSProperties = {
  fontFamily:    "'Manrope', system-ui, sans-serif",
  fontSize:      '15px',
  fontWeight:    700,
  color:         '#2b3437',
  margin:        0,
  letterSpacing: '-0.01em',
};

const optionRow: React.CSSProperties = {
  display:        'flex',
  alignItems:     'center',
  justifyContent: 'space-between',
  width:          '100%',
  padding:        '12px 14px',
  borderRadius:   '12px',
  border:         '1px solid rgba(43,52,55,0.08)',
  background:     '#fafbfc',
  color:          '#2b3437',
  fontSize:       '14px',
  fontWeight:     500,
  fontFamily:     "'Inter', system-ui, -apple-system, sans-serif",
  cursor:         'pointer',
  textAlign:      'start',
  lineHeight:     1.3,
  transition:     'background 0.15s, color 0.15s, border-color 0.15s',
};

const optionRowActive: React.CSSProperties = {
  background:   '#dde8ff',
  color:        '#005bc0',
  border:       '2px solid rgba(0,91,192,0.25)',
  fontWeight:   600,
};

const bottomBar: React.CSSProperties = {
  position:             'fixed',
  bottom:               0,
  left:                 '50%',
  transform:            'translateX(-50%)',
  width:                '100%',
  maxWidth:             '430px',
  padding:              '16px 20px max(var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)), 32px)',
  background:           'rgba(248,249,250,0.88)',
  backdropFilter:       'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  boxShadow:            '0 -8px 24px rgba(43,52,55,0.06)',
  zIndex:               50,
  boxSizing:            'border-box',
};

const primaryCta: React.CSSProperties = {
  display:       'block',
  width:         '100%',
  height:        '56px',
  borderRadius:  '16px',
  border:        'none',
  color:         '#f7f7ff',
  fontSize:      '17px',
  fontWeight:    700,
  fontFamily:    "'Manrope', system-ui, sans-serif",
  letterSpacing: '-0.01em',
  transition:    'background 0.2s, box-shadow 0.2s',
};
